import { useQuery } from '@apollo/client/react'
import type { ErrorLike } from '@apollo/client'

import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'

import {
  buildAllChannelsInsightsInput,
  isWaitingForInsightsPrerequisites,
} from './allChannelsInsightsVariables'
import type { AllChannelsInsightsOptions } from './allChannelsInsightsVariables'

// Metrics graph only. Decoupled from the summary + table query so the chart has
// its own loading/refetch lifecycle and never blocks the rest of the page.
export const AllChannelsTimeSeriesDocument = graphql(/* GraphQL */ `
  query AllChannelsTimeSeries($input: AllChannelsPostInsightsInput!) {
    allChannelsPostInsights(input: $input) {
      channelTimeSeries {
        channel {
          id
          name
          service
        }
        firstChannelDataCollectedAt
        primary {
          followers {
            date
            value
            variation
            source
          }
          reach {
            date
            value
            variation
            source
          }
          views {
            date
            value
            variation
            source
          }
          posts {
            date
            value
            variation
            source
          }
        }
        comparison {
          followers {
            date
            value
            source
          }
          reach {
            date
            value
            source
          }
          views {
            date
            value
            source
          }
          posts {
            date
            value
            source
          }
        }
      }
    }
  }
`)

export type AllChannelsTimeSeriesResult = ResultOf<
  typeof AllChannelsTimeSeriesDocument
>

export type ChannelTimeSeries =
  AllChannelsTimeSeriesResult['allChannelsPostInsights']['channelTimeSeries'][number]

interface UseAllChannelsTimeSeriesResult {
  channelTimeSeries: ChannelTimeSeries[]
  loading: boolean
  error: ErrorLike | undefined
}

export function useAllChannelsTimeSeries(
  options: AllChannelsInsightsOptions,
): UseAllChannelsTimeSeriesResult {
  const isWaitingForPrerequisites = isWaitingForInsightsPrerequisites(options)
  const { data, previousData, loading, error } = useQuery(
    AllChannelsTimeSeriesDocument,
    {
      variables: { input: buildAllChannelsInsightsInput(options) },
      fetchPolicy: 'cache-first',
      skip: options.skip || isWaitingForPrerequisites,
    },
  )

  // Use previous data while refetching to avoid layout jumps.
  const effectiveData = data ?? previousData

  return {
    channelTimeSeries:
      effectiveData?.allChannelsPostInsights?.channelTimeSeries ?? [],
    loading: loading || isWaitingForPrerequisites,
    error,
  }
}
